import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { VideoFormat } from "@omni/types/api/product/bff/shared/enums/videoFormat.enum";
import { ImageViewerImage } from "@omni/types/api/product/bff/shared/imageViewerImage.model";
import { ImageViewerVideo } from "@omni/types/api/product/bff/shared/imageViewerVideo.model";
import { ProductImageRendition } from "@omni/types/api/product/shared/enums/productImageRendition.enum";
import { Media } from "@omni/types/api/shared/bff/media.model";
import { attempt } from "@omni/utils/function.utils";

const mapMediaItem = (
    mediaList: Media[],
    altTextFallback: string
): ImageViewerImage | null => {
    let altText;
    const imageViewerImage: Partial<ImageViewerImage> = {};

    mediaList.forEach((media) => {
        altText = media.altText;

        switch (media.format) {
            case ProductImageRendition.MINI:
                imageViewerImage.thumbnail = media.url;
                break;

            case ProductImageRendition.MD:
                imageViewerImage.main = media.url;
                break;

            case ProductImageRendition.LG:
                imageViewerImage.zoom = media.url;
                break;

            case ProductImageRendition.XL:
                imageViewerImage.maxZoom = media.url;
                break;
        }
    });

    imageViewerImage.altText = altText || altTextFallback;

    if (
        imageViewerImage.main &&
        imageViewerImage.thumbnail &&
        imageViewerImage.altText
    ) {
        return imageViewerImage as ImageViewerImage;
    }

    return null;
};

const YOUTUBE_HOSTS = [
    "youtube.com",
    "www.youtube.com",
    "m.youtube.com",
    "youtube-nocookie.com",
    "www.youtube-nocookie.com"
];
const YOUTUBE_ID_PATHS = ["embed", "shorts", "v", "live"];
const VIMEO_HOSTS = ["vimeo.com", "www.vimeo.com", "player.vimeo.com"];

const parseUrl = (url: string) =>
    attempt<URL | undefined>(() => new URL(url), undefined);

const getPathSegments = (url: URL) => url.pathname.split("/").filter(Boolean);

const getYouTubeVideo = (url: URL) => {
    const [segment, id] = getPathSegments(url);
    let videoId: string | null | undefined;

    if (url.hostname === "youtu.be") {
        videoId = segment;
    } else if (YOUTUBE_HOSTS.includes(url.hostname)) {
        videoId = YOUTUBE_ID_PATHS.includes(segment)
            ? id
            : url.searchParams.get("v");
    }

    if (!videoId) return undefined;

    return {
        videoId,
        type:
            segment === "shorts"
                ? VideoFormat.YouTubeShort
                : VideoFormat.YouTube
    };
};

const getVimeoId = (url: URL) =>
    VIMEO_HOSTS.includes(url.hostname)
        ? getPathSegments(url).find((segment) => /^\d+$/.test(segment))
        : undefined;

const ImageViewerAdapter = (product: Product<ProductFieldSet.Full>) => {
    const images: ImageViewerImage[] = [];
    let videos: ImageViewerVideo[] = [];
    let usesDefaultImage = "0";

    if (product.mainImages) {
        product.mainImages.forEach((mainImage, index) => {
            const imageViewerImage = mapMediaItem(
                mainImage.medias,
                `${product.productTitle} ${index + 1}`
            );
            if (imageViewerImage) images.push(imageViewerImage);
        });
    }

    if (product.additionalImages) {
        product.additionalImages.forEach((mainImage, index) => {
            const imageViewerImage = mapMediaItem(
                mainImage.medias,
                `${product.productTitle} ${index + 1}`
            );
            if (imageViewerImage) images.push(imageViewerImage);
        });
    }

    if (
        images.length === 0 &&
        product.mainImages.length === 1 &&
        product.mainImages[0].medias.length > 0
    ) {
        const cloudMediaElement = product.mainImages[0].medias[0];
        images.push({
            thumbnail: cloudMediaElement.url,
            main: cloudMediaElement.url,
            altText: cloudMediaElement.altText
        });
        usesDefaultImage = "1";
    }

    videos = (product.videos || [])
        .filter((v) => v.name)
        .reduce(
            (acc, video) => {
                const url = parseUrl(video.url);

                if (!url) return acc;
                const youTubeVideo = getYouTubeVideo(url);
                const vimeoId = getVimeoId(url);

                if (youTubeVideo) {
                    const { videoId, type } = youTubeVideo;

                    acc.push({
                        videoId,
                        altText: video.name,
                        main: `https://www.youtube.com/embed/${videoId}`,
                        thumbnail: `https://img.youtube.com/vi/${videoId}/3.jpg`,
                        type
                    });
                } else if (vimeoId) {
                    acc.push({
                        videoId: vimeoId,
                        altText: video.name,
                        main: `https://player.vimeo.com/video/${vimeoId}`,
                        type: VideoFormat.Vimeo
                    });
                }

                return acc;
            },
            [] as Array<ImageViewerVideo>
        );

    return {
        sku: product.sku,
        usesDefaultImage: usesDefaultImage as "0" | "1",
        images,
        videos
    };
};

export { ImageViewerAdapter };
